import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import portalApi, { portalFileUrl, formatErr } from "./portalApi";
import { ArrowLeft, Download, Receipt, AlertCircle } from "lucide-react";

const cad = (n) => `CA$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fdate = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString("en-CA", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; } };

const STATUS_COLOR = {
  Paid: "bg-emerald-100 text-emerald-800",
  Sent: "bg-sky-100 text-sky-800",
  Viewed: "bg-sky-100 text-sky-800",
  "Partially Paid": "bg-amber-100 text-amber-800",
  Overdue: "bg-red-100 text-red-800",
};

export default function PortalInvoiceDetail() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await portalApi.get(`/portal/invoices/${id}`);
        setDoc(data);
      } catch (e) {
        setErr(formatErr(e));
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (err) return (
    <div className="max-w-md mx-auto bg-white border border-red-200 rounded-md p-6 text-center">
      <AlertCircle size={28} className="mx-auto text-red-500" />
      <p className="text-sm text-slate-700 mt-3">{err}</p>
      <Link to="/portal" className="inline-block mt-4 text-sm text-[#0F4C81] underline">Back</Link>
    </div>
  );
  if (!doc) return null;

  const balance = Math.max(Number(doc.total || 0) - Number(doc.amount_paid || 0), 0);

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="portal-invoice-detail">
      <Link to="/portal" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5">
        <ArrowLeft size={14} /> Back
      </Link>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-5 sm:px-7 py-6 border-b border-slate-100">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-medium">
                <Receipt size={13} /> Invoice
              </div>
              <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">{doc.number}</h1>
              <span className={`mt-3 inline-block text-[11px] px-2.5 py-1 rounded-full font-medium ${STATUS_COLOR[doc.status] || "bg-slate-100 text-slate-700"}`}>
                {doc.status}
              </span>
            </div>
            <a
              href={portalFileUrl(`/portal/invoices/${id}/pdf`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="portal-invoice-pdf-btn"
              className="self-start px-3 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50 flex items-center gap-2"
            >
              <Download size={14} /> Download PDF
            </a>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 text-sm">
            <Field label="Issued" value={fdate(doc.created_at)} />
            <Field label="Due" value={fdate(doc.due_date)} />
            <Field label="Total" value={<span className="tabular">{cad(doc.total)}</span>} />
            <Field label="Balance" value={<span className="tabular font-semibold text-slate-900">{cad(balance)}</span>} />
          </div>
        </div>

        <div className="px-5 sm:px-7 py-6">
          <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-3">Line items</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Description</th>
                  <th className="py-2 text-right">Qty</th>
                  <th className="py-2 text-right">Rate</th>
                  <th className="py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(doc.line_items || []).map((li, idx) => {
                  const sub = Number(li.quantity || 0) * Number(li.rate || 0);
                  const disc = li.line_discount_type === "percent"
                    ? sub * (Number(li.line_discount_value || 0) / 100)
                    : Number(li.line_discount_value || 0);
                  const total = Math.max(sub - disc, 0);
                  return (
                    <tr key={idx} className="border-b border-slate-100 align-top">
                      <td className="py-3 pr-3">
                        <div className="font-medium text-slate-900">{li.name}</div>
                        {li.description && <div className="text-xs text-slate-500 mt-0.5">{li.description}</div>}
                      </td>
                      <td className="py-3 text-right tabular text-slate-600">{li.quantity} {li.unit}</td>
                      <td className="py-3 text-right tabular text-slate-600">{cad(li.rate)}</td>
                      <td className="py-3 text-right tabular font-medium">{cad(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-5 ml-auto max-w-xs space-y-1.5 text-sm">
            <Row label="Subtotal" value={cad(doc.subtotal)} />
            {doc.invoice_discount_amount > 0 && <Row label="Discount" value={`− ${cad(doc.invoice_discount_amount)}`} />}
            <Row label={`HST (${doc.hst_rate}%)`} value={cad(doc.hst)} />
            <Row label="Total" value={cad(doc.total)} bold />
            {doc.amount_paid > 0 && <Row label="Paid" value={`− ${cad(doc.amount_paid)}`} />}
            <div className="border-t border-slate-200 pt-2 mt-2 flex justify-between text-base">
              <span className="font-semibold text-slate-900">Balance due</span>
              <span className="tabular font-bold text-slate-900">{cad(balance)}</span>
            </div>
          </div>

          {(doc.payments || []).length > 0 && (
            <div className="mt-6">
              <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">Payment history</h3>
              <ul className="bg-slate-50 border border-slate-200 rounded-md divide-y divide-slate-200 text-sm">
                {doc.payments.map((p, i) => (
                  <li key={i} className="px-4 py-2.5 flex justify-between">
                    <span className="text-slate-600">{fdate(p.paid_date)} · {p.method}{p.reference ? ` · ${p.reference}` : ""}</span>
                    <span className="tabular font-medium">{cad(p.amount)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {doc.notes && (
            <div className="mt-6 text-sm text-slate-600 border-l-2 border-slate-200 pl-4 whitespace-pre-wrap">
              {doc.notes}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="mt-1 text-slate-900">{value}</div>
    </div>
  );
}
function Row({ label, value, bold }) {
  return (
    <div className={`flex justify-between ${bold ? "font-semibold text-slate-900" : "text-slate-600"}`}>
      <span>{label}</span>
      <span className="tabular text-slate-900">{value}</span>
    </div>
  );
}
